import { IViewerFloor } from "../../../interfaces/IViewerFloor";
import { FloorSelectionExtensionCallbacks } from "./floorSelectionExtensionCallbacks";
import { FloorSelectionExtensionOptions } from "./floorSelectionExtensionOptions";
import { FloorSelectorSeparator } from "./floorSelectorSeparator";
import { FloorSelectorToolbar } from "./floorSelectorToolbar";
import { itemToShow } from "./itemToShow";
import { PagerButton } from "./PagerButton";

export class FloorSelectorPagerToolbar extends FloorSelectorToolbar {
	/**
	 * Maximum number of floor buttons to show on the toolbar.
	 *  The maximum includes the 2 pager buttons
	 */
	private maxNumberOfFloorButtons: number;
	/**
	 * Sorted, by level, floors list.
	 *  For calculations the floors will be sorted bottom (index 0) to top
	 */
	private sortedViewFloors: IViewerFloor[];
	private lowestFloorLevelIndex: number;
	private lowestViewFloor: IViewerFloor;
	private highestFloorLevelIndex: number;
	private highestViewFloor: IViewerFloor;

	public constructor(public options: FloorSelectionExtensionOptions, public floorToolBar: Autodesk.Viewing.UI.ToolBar) {
		super(options, floorToolBar);

		this.maxNumberOfFloorButtons = options.maxNumberOfFloorButtons ?? 7;
		this.lowestFloorLevelIndex = 0;
		this.highestFloorLevelIndex = options.viewerFloors.length - 1;
	}

	public async builtToolbarAsync(activeFloorLevel: number): Promise<void> {
		await this.initializeViewFloors();
		const activeFloorLevelIndex = this.getFloorLevelIndex(activeFloorLevel);
		const buttonCallbacks = {};
		const newSubToolbar = new Autodesk.Viewing.UI.ControlGroup(`${this.options.autodeskContainerId}-floor-subtoolbar`);

		const toolbarItemsFromTopToBottom = this.getToolbarFloorItems(activeFloorLevelIndex);

		this.addFloorItemsToTheToolbar(activeFloorLevelIndex, newSubToolbar, buttonCallbacks, toolbarItemsFromTopToBottom);

		this.updateFloorToolbar(buttonCallbacks, newSubToolbar);
	}

	private async initializeViewFloors(): Promise<void> {
		this.sortedViewFloors = await Promise.all<IViewerFloor>(this.options.viewerFloors.listAsync);
		this.sortedViewFloors.sort((a, b) => a.floor.level - b.floor.level);

		this.lowestViewFloor = this.sortedViewFloors[this.lowestFloorLevelIndex];
		this.highestViewFloor = this.sortedViewFloors[this.highestFloorLevelIndex];
	}

	private addFloorItemsToTheToolbar(
		activeFloorLevelIndex: number,
		newSubToolbar: Autodesk.Viewing.UI.ControlGroup,
		buttonCallbacks: {},
		toolbarItemsFromTopToBottom: ToolbarItem[]
	) {
		this.createFloorUpButton(activeFloorLevelIndex, newSubToolbar, buttonCallbacks);

		const onlyOneSeparatorIsShown = toolbarItemsFromTopToBottom.filter((item) => item.whatToShow === itemToShow.Separator).length < 2;

		toolbarItemsFromTopToBottom.forEach((toolbarItem) => {
			if (toolbarItem.whatToShow === itemToShow.Button) {
				const button = this.createToolbarButton(newSubToolbar, toolbarItem.viewFloor, buttonCallbacks);

				if (toolbarItem.floorLevelIndex === activeFloorLevelIndex) {
					this.activateFloor(button);
				}
			} else if (toolbarItem.whatToShow === itemToShow.Separator) {
				this.createToolbarSeparator(newSubToolbar, toolbarItem.viewFloor, onlyOneSeparatorIsShown);
			}
		});

		this.createFloorDownButton(activeFloorLevelIndex, newSubToolbar, buttonCallbacks);
	}

	private getToolbarFloorItems(activeFloorLevelIndex: number): ToolbarItem[] {
		const itemsToShow: ToolbarItem[] = [];

		this.sortedViewFloors.forEach((viewFloor) => {
			const floorLevelIndex = this.getFloorLevelIndex(viewFloor.floor.level);
			const whatToShow = this.itemToShow(viewFloor, activeFloorLevelIndex);

			// items are added to the list in reverse order, because the items are added
			//  to the toolbar top floor level first -> bottom level last
			itemsToShow.splice(0, 0, { viewFloor, floorLevelIndex, whatToShow });
		});

		return itemsToShow;
	}

	private updateFloorToolbar(buttonCallbacks: {}, newSubToolbar: Autodesk.Viewing.UI.ControlGroup) {
		const callbacks: FloorSelectionExtensionCallbacks = {
			activateFloor: (level: number): void => {
				buttonCallbacks[level]();
			},
		};

		this.options.callbackContext = callbacks;

		if (this.subToolbar != null) {
			this.floorToolBar.removeControl(this.subToolbar);
		}
		this.floorToolBar.addControl(newSubToolbar);
		this.subToolbar = newSubToolbar;
	}

	private createFloorDownButton(activeFloorLevelIndex: number, newSubToolbar: Autodesk.Viewing.UI.ControlGroup, buttonCallbacks: {}) {
		if (activeFloorLevelIndex > this.lowestFloorLevelIndex) {
			const oneLevelDownFloor = this.sortedViewFloors[activeFloorLevelIndex - 1];
			this.createToolbarPager(newSubToolbar, oneLevelDownFloor, buttonCallbacks, false, true);
		} else {
			this.createToolbarPager(newSubToolbar, this.lowestViewFloor, buttonCallbacks, false, false);
		}
	}

	private createFloorUpButton(activeFloorLevelIndex: number, newSubToolbar: Autodesk.Viewing.UI.ControlGroup, buttonCallbacks: {}) {
		if (activeFloorLevelIndex < this.highestFloorLevelIndex) {
			const oneLevelUpFloor = this.sortedViewFloors[activeFloorLevelIndex + 1];
			this.createToolbarPager(newSubToolbar, oneLevelUpFloor, buttonCallbacks, true, true);
		} else {
			this.createToolbarPager(newSubToolbar, this.highestViewFloor, buttonCallbacks, true, false);
		}
	}

	private getFloorLevelIndex(floorLevel: number): number {
		return this.sortedViewFloors.findIndex((viewFloor) => viewFloor.floor.level === floorLevel);
	}

	public itemToShow(viewerFloor: IViewerFloor, activeFloorLevelIndex: number): itemToShow {
		const viewerFloorLevelIndex = this.getFloorLevelIndex(viewerFloor.floor.level);

		if (viewerFloorLevelIndex === activeFloorLevelIndex) {
			// always show active floor button
			return itemToShow.Button;
		}

		if (viewerFloorLevelIndex === this.lowestFloorLevelIndex || viewerFloorLevelIndex === this.highestFloorLevelIndex) {
			// always show bottom and top floor buttons
			return itemToShow.Button;
		}

		const [buttonsToShowBelow, buttonsToShowAbove] = this.getNumberOfButtonsToShowBelowAndAboveTheActiveButton(activeFloorLevelIndex);

		if (viewerFloorLevelIndex < activeFloorLevelIndex) {
			if (viewerFloorLevelIndex >= activeFloorLevelIndex - buttonsToShowBelow) {
				return itemToShow.Button;
			} else if (viewerFloorLevelIndex === this.lowestFloorLevelIndex + 1) {
				// && activeFloorLevelIndex >= this.lowestFloorLevelIndex + 2
				return itemToShow.Separator;
			}
		} else {
			if (viewerFloorLevelIndex <= activeFloorLevelIndex + buttonsToShowAbove) {
				return itemToShow.Button;
			} else if (viewerFloorLevelIndex === this.highestFloorLevelIndex - 1) {
				//  && activeFloorLevelIndex <= this.highestFloorLevelIndex - 2
				return itemToShow.Separator;
			}
		}

		return itemToShow.None;
	}

	public getNumberOfButtonsToShowBelowAndAboveTheActiveButton(activeFloorLevelIndex: number): [number, number] {
		const maxButtonsToShowBesidesActiveButton = Math.max(0, this.maxNumberOfFloorButtons - 4);
		const distanceFromBottom = activeFloorLevelIndex - this.lowestFloorLevelIndex;
		const distanceFromTop = this.highestFloorLevelIndex - activeFloorLevelIndex;

		// try to position Buttons evenly above and below the active floor button
		//  if there are not enough positions below the active floor available, add positions at the top, and vice versa
		let buttonsToShowBelow = 0;
		let buttonsToShowAbove = 0;

		if (distanceFromBottom <= distanceFromTop) {
			buttonsToShowBelow = Math.min(Math.ceil(maxButtonsToShowBesidesActiveButton / 2), distanceFromBottom);
			buttonsToShowAbove = Math.min(maxButtonsToShowBesidesActiveButton - buttonsToShowBelow, distanceFromTop);
		} else {
			buttonsToShowAbove = Math.min(Math.ceil(maxButtonsToShowBesidesActiveButton / 2), distanceFromTop);
			buttonsToShowBelow = Math.min(maxButtonsToShowBesidesActiveButton - buttonsToShowAbove, distanceFromBottom);
		}

		if (distanceFromBottom > buttonsToShowBelow && distanceFromTop > buttonsToShowAbove) {
			// if the active button is positioned far away from the top and bottom
			// and the positions above and below are not evenly distributed, make it even
			if (buttonsToShowBelow > buttonsToShowAbove) {
				buttonsToShowBelow -= 1;
			} else if (buttonsToShowBelow < buttonsToShowAbove) {
				buttonsToShowAbove -= 1;
			}
		} else if (buttonsToShowBelow + buttonsToShowAbove < maxButtonsToShowBesidesActiveButton) {
			// if there are less positions taken than there are positions available
			//  check if position on either side should be added
			if (distanceFromBottom < distanceFromTop && buttonsToShowBelow < distanceFromBottom) {
				buttonsToShowBelow += 1;
			} else if (buttonsToShowAbove === distanceFromTop && buttonsToShowBelow < distanceFromBottom) {
				buttonsToShowBelow += 1;
			} else if (distanceFromTop < distanceFromBottom && buttonsToShowAbove < distanceFromTop) {
				buttonsToShowAbove += 1;
			} else if (buttonsToShowBelow === distanceFromBottom && buttonsToShowAbove < distanceFromTop) {
				buttonsToShowAbove += 1;
			}
		}

		return [buttonsToShowBelow, buttonsToShowAbove];
	}

	private createToolbarSeparator(subToolbar: Autodesk.Viewing.UI.ControlGroup, vf: IViewerFloor, singleSeparator: boolean) {
		const separator = new FloorSelectorSeparator(`${this.options.autodeskContainerId}-separator-${vf.floor.level}`, singleSeparator);

		subToolbar.addControl(separator);
	}

	private createToolbarPager(
		subToolbar: Autodesk.Viewing.UI.ControlGroup,
		vf: IViewerFloor,
		buttonCallbacks: {},
		up: boolean,
		enabled: boolean
	): PagerButton {
		const floor = vf.floor;
		const button = new PagerButton(`${this.options.autodeskContainerId}-switch-floor-pager-${floor.level}`, up, floor.level);

		button.active = enabled;

		if (enabled === true) {
			button.onClick = async (): Promise<void> => {
				this.options.onFloorChangedAsync(vf);

				this.activateFloor(button);
				this.builtToolbarAsync(button.floorLevel);
			};

			buttonCallbacks[floor.level] = () => this.activateFloor(button);
		}

		subToolbar.addControl(button);

		return button;
	}
}

interface ToolbarItem {
	viewFloor: IViewerFloor;
	floorLevelIndex: number;
	whatToShow: itemToShow;
}
